/*!
 * (c) 2026 Ocean Essentials Limited (HK). All rights reserved.
 * Proprietary code. Licensed, not sold. See LICENSE.md and Services Agreement.
 */
/**
 * ============================================================================
 * FIN SELECTOR V5 - MODAL VERSION CONTROLLER
 * ============================================================================
 * JavaScript controller for the modal wizard version (Version G).
 * Requires fin-selector-v5-core.js for state management and calculations.
 *
 * @version 5.0.0
 * @updated 2025-12-31
 * @author Clément Morandière - Senior Ecommerce Consultant - Waterspot Experts
 * ============================================================================
 */

(function () {
  "use strict";

  // ============================================================================
  // CONSTANTS
  // ============================================================================

  const SELECTORS = {
    modal: "#fin-selector-v5-modal",
    modalOverlay: "[data-fsv5-modal]",
    wizard: "[data-fsv5-wizard]",
    closeBtn: "[data-fsv5-modal-close]",
    openBtn: "[data-fsv5-open-modal]",
    prevBtn: "[data-fsv5-prev]",
    nextBtn: "[data-fsv5-next]",
    nextText: "[data-fsv5-next-text]",
    nextIcon: "[data-fsv5-next-icon]",
    progressStep: "[data-fsv5-progress-step]",
    stepContent: "[data-fsv5-step-content]",
    summary: "[data-fsv5-summary]",
    // Step-specific selectors
    skillOption: "[data-fsv5-skill]",
    disciplineOption: "[data-fsv5-discipline]",
    boxOption: "[data-fsv5-box]",
    antiweedToggle: "[data-fsv5-antiweed]",
    slider: "[data-fsv5-slider]",
    sliderFill: "[data-fsv5-slider-fill]",
    sliderValue: "[data-fsv5-slider-value]",
    recommendedText: "[data-fsv5-recommended-text]",
    // Compare
    compareToggle: "[data-fsv5-compare-toggle]",
    compareCount: "[data-fsv5-compare-count]",
    compareModal: "[data-fsv5-compare-modal]",
    productCompareBtn: "[data-fsv5-product-compare]",
  };

  const CLASSES = {
    modalOpen: "fsv5-modal-overlay--open",
    bodyLock: "fsv5-modal-open",
    stepActive: "fsv5-steps__item--active",
    stepCompleted: "fsv5-steps__item--completed",
    selected: "fsv5-skill-option--selected",
    disciplineSelected: "fsv5-discipline-option--selected",
    boxSelected: "fsv5-box-option--selected",
    toggleActive: "fsv5-toggle--active",
    stepEnterLeft: "fsv5-modal__step--enter-left",
    resultsActive: "fsv5-modal--results-active",
  };

  const TOTAL_STEPS = 5;
  const RESULTS_STEP = 4;

  // ============================================================================
  // STATE
  // ============================================================================

  let selectorInstance = null;
  let currentStep = 0;
  let config = {};
  let modalA11yState = null;
  let compareA11yState = null;
  let compareTrigger = null;
  let modalTrigger = null;

  // ============================================================================
  // INITIALIZATION
  // ============================================================================

  /**
   * Initialize the modal fin selector
   */
  function initFinSelectorModal() {
    const modal = document.querySelector(SELECTORS.modal);
    if (!modal) return;

    // Load configuration
    loadConfig();

    // Initialize core selector
    const wizard = modal.querySelector(SELECTORS.wizard);
    if (wizard && window.FinSelectorV5) {
      selectorInstance = FinSelectorV5.init(wizard, {
        onStateChange: handleStateChange,
        onStepChange: handleStepChange,
        onResultsUpdate: handleResultsUpdate,
        onCompareUpdate: handleCompareUpdate,
      });

      // Load products data if available
      loadProductsData();
    }

    // Setup event listeners
    setupModalTriggers();
    setupCloseHandlers();
    setupNavigationButtons();
    setupSelectionHandlers();
    setupSliderHandlers();
    setupCompareHandlers();

    // Initial UI state
    updateNavigationButtons(0);
    updateProgressBar(0);
  }

  /**
   * Load configuration from script tag
   */
  function loadConfig() {
    const configEl = document.getElementById("fsv5-modal-config");
    if (configEl) {
      try {
        config = JSON.parse(configEl.textContent);
      } catch (e) {
        console.warn("[FSV5 Modal] Could not parse config:", e);
        config = {};
      }
    }
  }

  /**
   * Load products data from JSON URL or embedded JSON
   */
  async function loadProductsData() {
    if (!selectorInstance) return;

    // Try URL-based loading first (static JSON file)
    const urlEl = document.querySelector("[data-fsv5-products-url]");
    if (urlEl) {
      try {
        const url = JSON.parse(urlEl.textContent);
        const response = await fetch(url);
        if (response.ok) {
          const products = await response.json();
          selectorInstance.loadProducts(products);
          return;
        }
      } catch (e) {
        console.error("[FSV5 Modal] Failed to fetch products:", e);
      }
    }

    // Fallback to inline JSON
    const dataEl = document.querySelector("[data-fsv5-products-data]");
    if (dataEl) {
      try {
        const data = JSON.parse(dataEl.textContent);
        if (data.products && Array.isArray(data.products)) {
          selectorInstance.loadProducts(data.products);
        }
      } catch (e) {
        console.warn("[FSV5 Modal] Could not parse products data:", e);
      }
    }
  }

  // ============================================================================
  // MODAL OPEN / CLOSE
  // ============================================================================

  /**
   * Setup modal trigger buttons
   */
  function setupModalTriggers() {
    document.addEventListener("click", function (e) {
      const trigger = e.target.closest(SELECTORS.openBtn);
      if (trigger) {
        e.preventDefault();
        openModal(trigger);
      }
    });
  }

  /**
   * Setup close handlers
   */
  function setupCloseHandlers() {
    const modal = document.querySelector(SELECTORS.modal);
    if (!modal) return;

    // Close button
    const closeBtn = modal.querySelector(SELECTORS.closeBtn);
    if (closeBtn) {
      closeBtn.addEventListener("click", closeModal);
    }

    // Click outside modal
    modal.addEventListener("click", function (e) {
      if (e.target === modal) {
        closeModal();
      }
    });

    // Escape key (the comparison modal, when open, handles Escape first)
    document.addEventListener("keydown", function (e) {
      if (e.key !== "Escape" || !isModalOpen()) return;
      const compareModal = document.querySelector(SELECTORS.compareModal);
      if (compareModal && compareModal.classList.contains(CLASSES.modalOpen)) return;
      closeModal();
    });
  }

  /**
   * Open the modal
   */
  function openModal(trigger) {
    const modal = document.querySelector(SELECTORS.modal);
    if (!modal) return;

    modal.classList.add(CLASSES.modalOpen);
    modal.setAttribute("aria-hidden", "false");
    document.body.classList.add(CLASSES.bodyLock);

    modalTrigger = trigger || document.activeElement;
    const closeBtn = modal.querySelector(SELECTORS.closeBtn);
    const a11y = window.UnifiberModalA11y;

    if (a11y) {
      modalA11yState = a11y.activate({
        roots: modal,
        trigger: trigger,
        focusTarget: closeBtn || modal.querySelector('button, [tabindex]:not([tabindex="-1"])'),
      });
    } else {
      const firstFocusable = modal.querySelector('button, [tabindex]:not([tabindex="-1"])');
      if (firstFocusable) {
        setTimeout(() => firstFocusable.focus(), 100);
      }
    }

    // Reset to first step
    goToStep(0);
  }

  /**
   * Close the modal
   */
  function closeModal() {
    const modal = document.querySelector(SELECTORS.modal);
    if (!modal) return;

    modal.classList.remove(CLASSES.modalOpen);
    modal.setAttribute("aria-hidden", "true");
    document.body.classList.remove(CLASSES.bodyLock);

    const a11y = window.UnifiberModalA11y;
    if (a11y && modalA11yState) {
      a11y.deactivate(modalA11yState);
      modalA11yState = null;
    } else {
      if (modalTrigger && document.contains(modalTrigger)) {
        modalTrigger.focus();
      }
      modalTrigger = null;
    }
  }

  /**
   * Check if modal is currently open
   */
  function isModalOpen() {
    const modal = document.querySelector(SELECTORS.modal);
    return modal && modal.classList.contains(CLASSES.modalOpen);
  }

  // ============================================================================
  // STEP NAVIGATION
  // ============================================================================

  /**
   * Setup navigation button handlers
   */
  function setupNavigationButtons() {
    const modal = document.querySelector(SELECTORS.modal);
    if (!modal) return;

    const prevBtn = modal.querySelector(SELECTORS.prevBtn);
    const nextBtn = modal.querySelector(SELECTORS.nextBtn);

    if (prevBtn) {
      prevBtn.addEventListener("click", function () {
        if (currentStep > 0) {
          goToStep(currentStep - 1, "left");
        }
      });
    }

    if (nextBtn) {
      nextBtn.addEventListener("click", function () {
        if (currentStep < TOTAL_STEPS - 1) {
          goToStep(currentStep + 1, "right");
        } else if (currentStep === RESULTS_STEP) {
          // On results step, could trigger add to cart or close
          closeModal();
        }
      });
    }
  }

  /**
   * Navigate to a specific step
   * @param {number} step - Step index (0-4)
   * @param {string} direction - 'left' or 'right' for animation
   */
  function goToStep(step, direction = "right") {
    const modal = document.querySelector(SELECTORS.modal);
    if (!modal) return;

    // Validate step
    step = Math.max(0, Math.min(step, TOTAL_STEPS - 1));

    // Hide all steps
    const steps = modal.querySelectorAll(SELECTORS.stepContent);
    steps.forEach((s) => {
      s.hidden = true;
      s.classList.remove(CLASSES.stepEnterLeft);
    });

    // Show target step
    const targetStep = modal.querySelector(`[data-fsv5-step-content="${step}"]`);
    if (targetStep) {
      targetStep.hidden = false;
      if (direction === "left") {
        targetStep.classList.add(CLASSES.stepEnterLeft);
      }
    }

    // Toggle results-active class for wider modal
    const wizard = modal.querySelector(SELECTORS.wizard);
    if (wizard) {
      wizard.classList.toggle(CLASSES.resultsActive, step === RESULTS_STEP);
    }

    // Update state
    currentStep = step;
    if (selectorInstance) {
      selectorInstance.updateState("currentStep", step);
    }

    // Update UI
    updateProgressBar(step);
    updateNavigationButtons(step);
    updateSummaryChips();

    // If going to results, trigger filtering
    if (step === RESULTS_STEP && selectorInstance) {
      selectorInstance.calculateRecommendedSize();
      selectorInstance.filterProducts();
    }
  }

  /**
   * Update the progress bar indicators
   * @param {number} step - Current step index
   */
  function updateProgressBar(step) {
    const modal = document.querySelector(SELECTORS.modal);
    if (!modal) return;

    const progressSteps = modal.querySelectorAll(SELECTORS.progressStep);
    progressSteps.forEach((el, i) => {
      const stepIndex = parseInt(el.dataset.fsv5ProgressStep, 10);

      el.classList.remove(CLASSES.stepActive, CLASSES.stepCompleted);

      if (stepIndex < step) {
        el.classList.add(CLASSES.stepCompleted);
      } else if (stepIndex === step) {
        el.classList.add(CLASSES.stepActive);
      }
    });
  }

  /**
   * Update navigation buttons based on current step
   * @param {number} step - Current step index
   */
  function updateNavigationButtons(step) {
    const modal = document.querySelector(SELECTORS.modal);
    if (!modal) return;

    const prevBtn = modal.querySelector(SELECTORS.prevBtn);
    const nextBtn = modal.querySelector(SELECTORS.nextBtn);
    const nextText = modal.querySelector(SELECTORS.nextText);
    const nextIcon = modal.querySelector(SELECTORS.nextIcon);

    // Previous button
    if (prevBtn) {
      prevBtn.disabled = step === 0;
    }

    // Next button text
    if (nextText) {
      if (step === TOTAL_STEPS - 2) {
        // Step before results
        nextText.textContent = config.findFinsText || "Find My Fins";
      } else if (step === RESULTS_STEP) {
        // On results step
        nextText.textContent = "Close";
      } else {
        nextText.textContent = config.nextText || "Next";
      }
    }

    // Hide arrow icon on last step
    if (nextIcon) {
      nextIcon.style.display = step === RESULTS_STEP ? "none" : "";
    }
  }

  // ============================================================================
  // SELECTION HANDLERS
  // ============================================================================

  /**
   * Setup all selection handlers
   */
  function setupSelectionHandlers() {
    const modal = document.querySelector(SELECTORS.modal);
    if (!modal) return;

    // Skill selection
    modal.addEventListener("click", function (e) {
      const skillBtn = e.target.closest(SELECTORS.skillOption);
      if (skillBtn) {
        handleSkillSelection(skillBtn);
      }
    });

    // Discipline selection
    modal.addEventListener("click", function (e) {
      const discBtn = e.target.closest(SELECTORS.disciplineOption);
      if (discBtn) {
        handleDisciplineSelection(discBtn);
      }
    });

    // Box type selection
    modal.addEventListener("click", function (e) {
      const boxBtn = e.target.closest(SELECTORS.boxOption);
      if (boxBtn && boxBtn.getAttribute("aria-disabled") !== "true") {
        handleBoxSelection(boxBtn);
      }
    });

    // Antiweed toggle
    modal.addEventListener("click", function (e) {
      const toggle = e.target.closest(SELECTORS.antiweedToggle);
      if (toggle) {
        handleAntiweedToggle(toggle);
      }
    });
  }

  /**
   * Handle skill level selection
   */
  function handleSkillSelection(btn) {
    const modal = document.querySelector(SELECTORS.modal);
    const skill = btn.dataset.fsv5Skill;

    // Update UI
    modal.querySelectorAll(SELECTORS.skillOption).forEach((el) => {
      el.classList.remove(CLASSES.selected);
      el.setAttribute("aria-pressed", "false");
    });
    btn.classList.add(CLASSES.selected);
    btn.setAttribute("aria-pressed", "true");

    // Update state
    if (selectorInstance) {
      selectorInstance.updateState("skillLevel", skill);
    }

    updateSummaryChips();
  }

  /**
   * Handle discipline selection
   */
  function handleDisciplineSelection(btn) {
    const modal = document.querySelector(SELECTORS.modal);
    const discipline = btn.dataset.fsv5Discipline;

    // Update UI
    modal.querySelectorAll(SELECTORS.disciplineOption).forEach((el) => {
      el.classList.remove(CLASSES.disciplineSelected);
      el.setAttribute("aria-pressed", "false");
    });
    btn.classList.add(CLASSES.disciplineSelected);
    btn.setAttribute("aria-pressed", "true");

    // Update state
    if (selectorInstance) {
      selectorInstance.updateState("discipline", discipline);

      // Update box compatibility based on discipline
      updateBoxCompatibility(discipline);
    }

    updateSummaryChips();
  }

  /**
   * Handle box type selection
   */
  function handleBoxSelection(btn) {
    const modal = document.querySelector(SELECTORS.modal);
    const boxType = btn.dataset.fsv5Box;

    // Update UI
    modal.querySelectorAll(SELECTORS.boxOption).forEach((el) => {
      el.classList.remove(CLASSES.boxSelected);
      el.setAttribute("aria-pressed", "false");
    });
    btn.classList.add(CLASSES.boxSelected);
    btn.setAttribute("aria-pressed", "true");

    // Update state
    if (selectorInstance) {
      selectorInstance.updateState("boxType", boxType);
    }

    updateSummaryChips();
  }

  /**
   * Handle antiweed toggle
   */
  function handleAntiweedToggle(toggle) {
    const isActive = toggle.classList.toggle(CLASSES.toggleActive);
    toggle.setAttribute("aria-pressed", isActive ? "true" : "false");

    // Update state
    if (selectorInstance) {
      selectorInstance.updateState("antiweed", isActive);
    }

    updateSummaryChips();
  }

  /**
   * Update box type compatibility based on discipline
   */
  function updateBoxCompatibility(discipline) {
    const modal = document.querySelector(SELECTORS.modal);
    if (!modal || !selectorInstance) return;

    const boxOptions = modal.querySelectorAll(SELECTORS.boxOption);
    const compatibility = selectorInstance.getBoxCompatibility(discipline);

    boxOptions.forEach((btn) => {
      const boxType = btn.dataset.fsv5Box;
      const isCompatible = compatibility[boxType] !== false;

      btn.dataset.fsv5BoxCompatible = isCompatible ? "true" : "false";

      if (isCompatible) {
        btn.removeAttribute("aria-disabled");
        btn.classList.remove("fsv5-box-option--disabled");
      } else {
        btn.setAttribute("aria-disabled", "true");
        btn.classList.add("fsv5-box-option--disabled");
        btn.classList.remove(CLASSES.boxSelected);
        btn.setAttribute("aria-pressed", "false");
      }
    });
  }

  // ============================================================================
  // SLIDER HANDLERS
  // ============================================================================

  /**
   * Setup slider input handlers
   */
  function setupSliderHandlers() {
    const modal = document.querySelector(SELECTORS.modal);
    if (!modal) return;

    const sliders = modal.querySelectorAll(SELECTORS.slider);
    sliders.forEach((slider) => {
      slider.addEventListener("input", handleSliderInput);
      slider.addEventListener("change", handleSliderChange);
    });
  }

  /**
   * Handle slider input (real-time)
   */
  function handleSliderInput(e) {
    const slider = e.target;
    const sliderType = slider.dataset.fsv5Slider;
    const value = parseFloat(slider.value);

    // Update fill
    updateSliderFill(slider);

    // Update value display
    updateSliderValueDisplay(slider, value);
  }

  /**
   * Handle slider change (on release)
   */
  function handleSliderChange(e) {
    const slider = e.target;
    const sliderType = slider.dataset.fsv5Slider;
    const value = parseFloat(slider.value);

    // Update state
    if (selectorInstance) {
      const stateKey = sliderTypeToStateKey(sliderType);
      if (stateKey) {
        selectorInstance.updateState(stateKey, value);
      }

      // Recalculate recommended size
      const recommendedSize = selectorInstance.calculateRecommendedSize();
      const range = selectorInstance.calculateRange(recommendedSize);
      updateRecommendedBadge(range);
    }

    updateSummaryChips();
  }

  /**
   * Update slider fill based on value
   */
  function updateSliderFill(slider) {
    const min = parseFloat(slider.min);
    const max = parseFloat(slider.max);
    const value = parseFloat(slider.value);
    const percentage = ((value - min) / (max - min)) * 100;

    const sliderType = slider.dataset.fsv5Slider;
    const fillEl = document.querySelector(`[data-fsv5-slider-fill="${sliderType}"]`);
    if (fillEl) {
      fillEl.style.width = `${percentage}%`;
    }
  }

  /**
   * Update slider value display
   */
  function updateSliderValueDisplay(slider, value) {
    const sliderType = slider.dataset.fsv5Slider;
    const valueEl = document.querySelector(`[data-fsv5-slider-value="${sliderType}"]`);
    if (valueEl) {
      let displayValue = value;
      let unit = "";

      switch (sliderType) {
        case "sail_size":
          displayValue = value.toFixed(1);
          unit = "m<sup>2</sup>";
          break;
        case "board_volume":
          displayValue = Math.round(value);
          unit = "L";
          break;
        case "board_width":
          displayValue = Math.round(value);
          unit = "cm";
          break;
      }

      valueEl.innerHTML = `${displayValue}<small>${unit}</small>`;
    }
  }

  /**
   * Update recommended range badge
   */
  function updateRecommendedBadge(range) {
    const badgeText = document.querySelector(SELECTORS.recommendedText);
    if (badgeText && range && range.min && range.max) {
      badgeText.textContent = `Recommended: ${range.min}-${range.max} cm`;
    }
  }

  /**
   * Convert slider type to state key
   */
  function sliderTypeToStateKey(sliderType) {
    const mapping = {
      sail_size: "sailSize",
      board_volume: "boardVolume",
      board_width: "boardWidth",
    };
    return mapping[sliderType] || null;
  }

  // ============================================================================
  // SUMMARY CHIPS
  // ============================================================================

  /**
   * Update summary chips in footer
   */
  function updateSummaryChips() {
    const modal = document.querySelector(SELECTORS.modal);
    if (!modal || !selectorInstance) return;

    const summaryEl = modal.querySelector(SELECTORS.summary);
    if (!summaryEl) return;

    const state = selectorInstance.getState();
    const chips = [];

    if (state.skillLevel) {
      chips.push(createChip("Skill", capitalizeFirst(state.skillLevel)));
    }

    if (state.discipline) {
      chips.push(createChip("Style", selectorInstance.getDisciplineLabel(state.discipline)));
    }

    if (state.boxType) {
      chips.push(createChip("Box", selectorInstance.getBoxTypeLabel(state.boxType)));
    }

    if (state.sailSize) {
      chips.push(createChip("Sail", `${state.sailSize}m2`));
    }

    if (state.antiweed) {
      chips.push(createChip("Type", "Antiweed"));
    }

    summaryEl.innerHTML = chips.join("");
  }

  /**
   * Create a summary chip HTML
   */
  function createChip(label, value) {
    return `
      <span class="fsv5-summary-chip">
        <span class="fsv5-summary-chip__label">${label}:</span>
        <span class="fsv5-summary-chip__value">${value}</span>
      </span>
    `;
  }

  /**
   * Capitalize first letter
   */
  function capitalizeFirst(str) {
    return str ? str.charAt(0).toUpperCase() + str.slice(1) : "";
  }

  // ============================================================================
  // COMPARE FUNCTIONALITY
  // ============================================================================

  /**
   * Setup compare handlers
   */
  function setupCompareHandlers() {
    const modal = document.querySelector(SELECTORS.modal);
    if (!modal) return;

    // Compare toggle button (opens compare modal)
    modal.addEventListener("click", function (e) {
      const toggle = e.target.closest(SELECTORS.compareToggle);
      if (toggle) {
        openCompareModal(toggle);
      }
    });

    const compareModal = document.querySelector(SELECTORS.compareModal);
    if (compareModal) {
      const closeBtn = compareModal.querySelector(SELECTORS.closeBtn);
      if (closeBtn) {
        closeBtn.addEventListener("click", closeCompareModal);
      }

      compareModal.addEventListener("click", function (e) {
        if (e.target === compareModal) {
          closeCompareModal();
        }
      });

      document.addEventListener("keydown", function (e) {
        if (e.key === "Escape" && compareModal.classList.contains(CLASSES.modalOpen)) {
          closeCompareModal();
        }
      });
    }

    // Product compare buttons
    modal.addEventListener("click", function (e) {
      const compareBtn = e.target.closest(SELECTORS.productCompareBtn);
      if (compareBtn) {
        const productId = compareBtn.dataset.productId;
        if (productId && selectorInstance) {
          selectorInstance.toggleCompare(productId);
        }
      }
    });
  }

  /**
   * Open compare modal
   */
  function openCompareModal(trigger) {
    const compareModal = document.querySelector(SELECTORS.compareModal);
    if (!compareModal) return;

    compareModal.classList.add(CLASSES.modalOpen);
    compareModal.setAttribute("aria-hidden", "false");

    compareTrigger = trigger || null;
    if (compareTrigger) compareTrigger.setAttribute("aria-expanded", "true");

    const closeBtn = compareModal.querySelector(SELECTORS.closeBtn);
    const a11y = window.UnifiberModalA11y;

    if (a11y) {
      compareA11yState = a11y.activate({
        roots: compareModal,
        trigger: trigger,
        focusTarget: closeBtn,
      });
    }
  }

  function closeCompareModal() {
    const compareModal = document.querySelector(SELECTORS.compareModal);
    if (!compareModal) return;

    compareModal.classList.remove(CLASSES.modalOpen);
    compareModal.setAttribute("aria-hidden", "true");

    if (compareTrigger) {
      compareTrigger.setAttribute("aria-expanded", "false");
      compareTrigger = null;
    }

    const a11y = window.UnifiberModalA11y;
    if (a11y && compareA11yState) {
      a11y.deactivate(compareA11yState);
      compareA11yState = null;
    }
  }

  /**
   * Update compare count badge
   */
  function updateCompareCount(count) {
    const countEl = document.querySelector(SELECTORS.compareCount);
    if (countEl) {
      countEl.textContent = count;
    }
  }

  // ============================================================================
  // STATE CHANGE HANDLERS
  // ============================================================================

  /**
   * Handle state changes from core
   */
  function handleStateChange(key, newValue, oldValue) {
    // Update UI based on state changes
    updateSummaryChips();
  }

  /**
   * Handle step changes
   */
  function handleStepChange(previousStep, currentStepValue) {
    // Could add analytics tracking here
  }

  /**
   * Handle results update
   */
  function handleResultsUpdate(products) {
    // Results are handled by the step-results snippet
  }

  /**
   * Handle compare update
   */
  function handleCompareUpdate(action, productId, selectedIds) {
    updateCompareCount(selectedIds.length);
  }

  // ============================================================================
  // INITIALIZATION ON DOM READY
  // ============================================================================

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initFinSelectorModal);
  } else {
    initFinSelectorModal();
  }

  // Expose API for external use
  window.FinSelectorV5Modal = {
    open: openModal,
    close: closeModal,
    goToStep: goToStep,
    getInstance: function () {
      return selectorInstance;
    },
  };
})();
